import { appTabs } from '@client/AppRoute';
import { loadingAtom } from '@client/atoms/loadingAtom';
import { tabAtom } from '@client/atoms/tabAtom';
import { authClient } from '@client/lib/authClient';
import {
  Box,
  Group,
  Loader,
  Menu,
  Tabs,
  Text,
  UnstyledButton,
} from '@mantine/core';
import cx from 'clsx';
import { useAtomValue } from 'jotai';
import { ArrowLeftRight, ChevronDown, LogOut, Settings } from 'lucide-react';
import { type FC, memo, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';

const TopBar = memo(() => {
  const { data: session } = authClient.useSession();
  const [, navigate] = useLocation();
  const tab = useAtomValue(tabAtom);
  const isLoading = useAtomValue(loadingAtom);

  return (
    <Group
      justify="space-between"
      px="md"
    >
      {isLoading && (
        <Box className="absolute left-1/2 -translate-x-1/2 -top-px px-3 py-1 rounded-b-xl bg-slate-400">
          <Loader
            color="white"
            size="sm"
            type="dots"
          />
        </Box>
      )}
      <Group
        align="end"
        gap="lg"
      >
        <Text className="leading-14 text-gray-600 font-bold text-2xl min-w-52">
          Suspense
        </Text>
        <Tabs
          classNames={{
            list: 'before:hidden',
            root: 'relative',
            tab: cx(
              `underline underline-offset-5 relative font-medium h-10 -bottom-[1px] before:bg-slate-300
              after:bg-slate-300 data-active:bg-white data-active:border-slate-300
              data-active:border-b-transparent data-active:shadow-slate-400/20 data-active:shadow-[0_-1px_2px]
              data-active:no-underline`,
            ),
          }}
          value={tab}
          variant="outline"
        >
          <Tabs.List>
            {appTabs.keys().map((slug) => {
              const tabFromSlug = appTabs.get(slug);
              if (!tabFromSlug) {
                return null;
              }

              return (
                <Tabs.Tab
                  component={Link}
                  key={slug}
                  leftSection={tabFromSlug.icon}
                  // @ts-expect-error meh
                  to={tab.href}
                  value={slug}
                >
                  {tabFromSlug.title}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Tabs>
      </Group>

      <Menu
        position="bottom-end"
        transitionProps={{ transition: 'pop-top-right' }}
        width={260}
        withinPortal
      >
        <Menu.Target>
          <UnstyledButton
            className={`text-black dark:text-gray-200 px-3 py-2 rounded-md transition-colors hover:bg-white
              dark:hover:bg-gray-800 data-expanded:bg-white dark:data-expanded:bg-gray-800`}
          >
            <Group gap={7}>
              <Text
                fw={500}
                lh={1}
                mr={3}
                size="sm"
              >
                {session?.user.name}
              </Text>
              <ChevronDown size={12} />
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Settings</Menu.Label>
          <Menu.Item leftSection={<Settings size={16} />}>
            Account settings
          </Menu.Item>
          <Menu.Item leftSection={<ArrowLeftRight size={16} />}>
            Change account
          </Menu.Item>
          <Menu.Item
            leftSection={<LogOut size={16} />}
            onClick={() => {
              authClient.signOut();
              navigate('/signin', { replace: true });
            }}
          >
            Logout
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
});

export const Layout: FC<{ readonly children: ReactNode }> = ({ children }) => {
  return (
    <div className="inset-0 absolute flex flex-col">
      <div className="bg-slate-200/80 border-b border-b-slate-300 shadow-slate-400/25 shadow-[inset_0_-1px_2px]">
        <TopBar />
      </div>
      {children}
    </div>
  );
};
